import { ICpEntity } from '@se/cp.engine';
import { Parser, SUPPORTED_FORMULAS } from '@ebit/hot-formula-parser';
import { isNotEmpty } from '../export-clinical-eval/supports';
import {
    ApplyFilterParams,
    ApplyFindIndexParams,
    ApplyFindParams,
    ApplyMapParams,
    ApplyPivotFilterParams,
    ApplyPivotMultiFilterParams,
} from './interfaces';

export const applyMap = (sourceArray: Array<any>, propToMap: string, fnName: string | null) => {
    const parser = new Parser();
    if (!Array.isArray(sourceArray)) return null;
    if (!!fnName && !SUPPORTED_FORMULAS.find((formula: string) => formula === fnName)) return null;
    if (!fnName) return sourceArray.map((entry) => entry[propToMap]);
    return parser._callFunction(fnName, propToMap ? sourceArray.map((entry) => entry[propToMap]) : sourceArray);
};

export const applyFind = (sourceArray: Array<any>, propToFind: string, matchValue: unknown, propToGet: string) => {
    if (!Array.isArray(sourceArray)) return null;
    const found = sourceArray.find((item) => item?.[propToFind] === matchValue);
    return (found && propToGet ? found[propToGet] : found) ?? null;
};

export const applyFindIndex = (sourceArray: Array<any>, propToFind: string, matchValue: unknown): number => {
    if (!Array.isArray(sourceArray)) return -1;
    const foundIndex = sourceArray.findIndex((item) => item?.[propToFind] === matchValue);
    return foundIndex ?? -1;
};

export const innerDeepGetProp = (obj: any, propList: string[]): any => {
    if (obj == null || typeof obj !== 'object') return null; // Aggiunta questa condizione per gestire gli input non validi
    if (!Array.isArray(propList)) return null;
    return propList.reduce((prev, curr) => {
        if (typeof prev !== 'object' || !prev) return null;
        return prev[curr];
    }, obj);
};


/**
 *
 * @param sourceArray
 * @param bindingToFind x.y.z
 * @param matchValue
 * @param propToGet se non specificata ritorna l'oggetto trovato
 */
//la funzione cerca un oggetto all'interno dell'array,trova una proprietà specificata all'interno dell'oggetto
//e restituire il suo valore corrispondente o l'oggetto in se  altrimenti restituisce null.
export const applyFindDeep = (
    sourceArray: Array<any>,
    bindingToFind: string,
    matchValue: unknown,
    propToGet: string,
) => {
    if (!Array.isArray(sourceArray)) return null;
    const propsList = bindingToFind.split('.');
    const found = sourceArray.find((item) => innerDeepGetProp(item, propsList) === matchValue);
    return (found && propToGet ? found[propToGet] : found) ?? null;
};
//funzione simile alla precedente applyFindDeep,
//ma anziché restituire l'oggetto trovato o una sua proprietà, restituisce l'indice dell'oggetto trovato nell'array.
export const applyFindIndexDeep = (sourceArray: Array<any>, bindingToFind: string, matchValue: unknown): number => {
    if (!Array.isArray(sourceArray)) return -1;
    const propsList = bindingToFind.split('.');
    const foundIndex = sourceArray.findIndex((item) => innerDeepGetProp(item, propsList) === matchValue);
    return foundIndex ?? -1;
};

export const applyFilter = (sourceArray: Array<any>, propToCompare: string, matchValue: unknown) => {
    if (!Array.isArray(sourceArray)) return [];
    return sourceArray.filter((item) => item[propToCompare] === matchValue);
};

export const applyPivotFilter = (
    sourceArray: Array<any>,
    pivotSourceArray: Array<any>,
    fkIn: string,
    fkOut: string,
    matchValue: unknown,
) => {
    if (!Array.isArray(sourceArray)) return [];
    const foreignKeys = pivotSourceArray
        .filter((item) => item[fkIn] === matchValue)
        .map((item) => item[fkOut])
        .filter(isNotEmpty);
    const foreignKeysSet = new Set(foreignKeys);
    return sourceArray.filter((item) => foreignKeysSet.has(item.Id));
};

export const applyPivotMultiFilter = (
    sourceArray: Array<any>,
    pivotSourceArray: Array<any>,
    fkIn: string,
    fkOut: string,
    matchEntities: ICpEntity[],
    propertyPath: string,
) => {
    if (!Array.isArray(sourceArray)) return [];
    const ids = matchEntities.map((entity) => entity.getNestedPropertyValue(propertyPath));
    const foreignKeys = pivotSourceArray
        .filter((item) => ids.includes(item[fkIn]))
        .map((item) => item[fkOut])
        .filter(isNotEmpty);
    const foreignKeysSet = new Set(foreignKeys);
    return sourceArray.filter((item) => foreignKeysSet.has(item.Id));
};

export const arrayFunctionsMap: Record<string, Function> = {
    APPLY_MAP: (params: ApplyMapParams) => applyMap(...params),
    APPLY_FIND: (params: ApplyFindParams) => applyFind(...params),
    APPLY_FIND_INDEX: (params: ApplyFindIndexParams) => applyFindIndex(...params),
    APPLY_FIND_DEEP: (params: ApplyFindParams) => applyFindDeep(...params),
    APPLY_FIND_INDEX_DEEP: (params: ApplyFindIndexParams) => applyFindIndexDeep(...params),
    APPLY_FILTER: (params: ApplyFilterParams) => applyFilter(...params),
    APPLY_PIVOT_FILTER: (params: ApplyPivotFilterParams) => applyPivotFilter(...params),
    APPLY_PIVOT_MULTI_FILTER: (params: ApplyPivotMultiFilterParams) => applyPivotMultiFilter(...params),
};
